import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Save, Play, CheckSquare, Square, ChevronDown, ChevronUp, RefreshCw, Plus, Filter } from "lucide-react";
import { getStoreId } from "../utils/auth";
import * as logger from "../utils/logger";
import { showToast } from "../utils/toast";
import "./SEOOptimizerAutomation.css";
import BulkOptimizeGuidedTour from "./BulkOptimizeGuidedTour";
import BulkTourReplayHintFlyout from "./BulkTourReplayHintFlyout";
import { hasCompletedBulkOptimizeTour } from "../utils/bulkOptimizeTourStorage";

const SEOOptimizerAutomation = forwardRef(function SEOOptimizerAutomation(_, ref) {
  const { t } = useTranslation();
  const tn = (key, opts) => t(`seoOptimizerAutomation.${key}`, opts);
  const apiUrl = import.meta.env.VITE_BACKEND_URL;
  const hashSiteId = getStoreId();
  
  // Content Type Selection (for templates)
  const [contentType, setContentType] = useState('Products');
  
  // Selection Entity Type (for selecting items to apply templates to)
  const [selectedEntityType, setSelectedEntityType] = useState('Products');
  
  // Template state - separate for each content type
  const [productTemplates, setProductTemplates] = useState({
    title: '',
    meta: '',
    alt: '',
    cruiseControl: false
  });
  const [categoryTemplates, setCategoryTemplates] = useState({
    title: '',
    meta: '',
    cruiseControl: false
  });
  // Store-level: auto-validate after bulk updates (from seo-cache/settings)
  const [autoValidateAfterBulkUpdate, setAutoValidateAfterBulkUpdate] = useState(false);
  
  // Get current templates based on contentType
  const getCurrentTemplates = () => {
    switch (contentType) {
      case 'Products':
        return productTemplates;
      case 'Categories':
        return categoryTemplates;
      default:
        return productTemplates;
    }
  };
  
  const templates = getCurrentTemplates();
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  
  // Preview state
  const [currentTitle, setCurrentTitle] = useState('[Sample] Oak Cheese Grater');
  const [currentMeta, setCurrentMeta] = useState('Sample meta description for the product');
  const [currentAlt, setCurrentAlt] = useState('Sample alt text for product image');
  const [newTitle, setNewTitle] = useState('');
  const [newMeta, setNewMeta] = useState('');
  const [newAlt, setNewAlt] = useState('');
  
  // Product selection state
  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productLimit, setProductLimit] = useState(50);
  const [productTotal, setProductTotal] = useState(0);
  const [productSearch, setProductSearch] = useState('');
  const [productCategory, setProductCategory] = useState('');
  /** Ecwid storefront-visible products only (enabled=true) — Bulk product list */
  const [productListingFilter, setProductListingFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Store name for {store_name} in template preview
  const [storeName, setStoreName] = useState('');

  // Queue status
  const [queueStatus, setQueueStatus] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0
  });
  const [isRunningTemplate, setIsRunningTemplate] = useState(false);
  const [isApplyingToCategory, setIsApplyingToCategory] = useState(false);
  const [updatingRecordId, setUpdatingRecordId] = useState(null);
  
  // UI state - start collapsed; only Edit Template button toggles
  const [showTemplateSection, setShowTemplateSection] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFields, setPendingFields] = useState(new Set());
  
  // Refs to prevent duplicate fetches (e.g. React Strict Mode)
  const loadAllTemplatesInFlightRef = useRef(false);
  const loadCacheSettingsInFlightRef = useRef(false);
  const fetchCategoriesInFlightRef = useRef(false);
  const fetchProductsInFlightRef = useRef(false);
  const fetchQueueStatusInFlightRef = useRef(false);
  const bulkUpdateButtonRef = useRef(null);

  const [bulkTourRun, setBulkTourRun] = useState(false);
  const [bulkTourReplayFlyout, setBulkTourReplayFlyout] = useState(false);

  const beginBulkOptimizeTour = useCallback(() => {
    setBulkTourRun(true);
  }, []);

  useImperativeHandle(ref, () => ({ beginBulkOptimizeTour }), [beginBulkOptimizeTour]);

  const handleBulkTourClose = useCallback(() => {
    setBulkTourRun(false);
  }, []);

  const handleBulkTourExit = useCallback(() => {
    setBulkTourReplayFlyout(true);
  }, []);

  const handleBulkTourReplayFlyoutFinished = useCallback(() => {
    setBulkTourReplayFlyout(false);
  }, []);

  useEffect(() => {
    if (bulkTourRun) {
      setShowTemplateSection(true);
    }
  }, [bulkTourRun]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (hasCompletedBulkOptimizeTour()) return;
    const tid = window.setTimeout(() => setBulkTourRun(true), 1100);
    return () => window.clearTimeout(tid);
  }, []);
  
  // Dynamic variables for Products (based on Ecwid API) – include existing SEO for template reuse (BigCommerce parity)
  const productVariables = [
    { label: tn('variableProductName'), value: '{product_name}' },
    { label: tn('variableSku'), value: '{sku}' },
    { label: tn('variablePrice'), value: '{price}' },
    { label: tn('variablePriceFormatted'), value: '{price_formatted}' },
    { label: tn('variableDescription'), value: '{description}' },
    { label: tn('variableCategoryName'), value: '{category_name}' },
    { label: tn('variableBrand'), value: '{brand}' },
    { label: tn('variableMpn'), value: '{mpn}' },
    { label: tn('variableUpc'), value: '{upc}' },
    { label: tn('variableCondition'), value: '{condition}' },
    { label: tn('variableProductType'), value: '{product_type}' },
    { label: tn('variableStoreName'), value: '{store_name}' },
    { label: tn('variableExistingSeoTitle'), value: '{existing_seo_title}' },
    { label: tn('variableExistingMetaDescription'), value: '{existing_meta_description}' },
  ];
  
  // Dynamic variables for Categories (based on Ecwid API)
  const categoryVariables = [
    { label: tn('variableCategoryName'), value: '{category_name}' },
    { label: tn('variableCategoryDescription'), value: '{category_description}' },
    { label: tn('variableParentCategory'), value: '{parent_category}' },
    { label: tn('variableStoreName'), value: '{store_name}' },
  ];
  
  // Get dynamic variables based on contentType
  const getDynamicVariables = () => {
    switch (contentType) {
      case 'Products':
        return productVariables;
      case 'Categories':
        return categoryVariables;
      default:
        return productVariables;
    }
  };
  
  const dynamicVariables = getDynamicVariables();
  
  // Replace variables in template
  const replaceVariables = (template, type = contentType) => {
    if (!template) return '';
    let preview = template;
    
    if (type === 'Products') {
      preview = preview
        .replace(/{product_name}/g, '[Sample] Oak Cheese Grater')
        .replace(/{sku}/g, 'SKU-12345')
        .replace(/{price}/g, '$29.99')
        .replace(/{price_formatted}/g, '$29.99')
        .replace(/{description}/g, 'High-quality kitchen tool')
        .replace(/{category_name}/g, 'Kitchen Tools')
        .replace(/{mpn}/g, 'MPN-12345')
        .replace(/{upc}/g, '123456789012')
        .replace(/{condition}/g, 'New')
        .replace(/{product_type}/g, 'Physical')
        .replace(/{store_name}/g, storeName || 'My Store')
        .replace(/{existing_seo_title}/g, 'Sample SEO title')
        .replace(/{existing_meta_description}/g, 'Sample meta description for the product');
    } else if (type === 'Categories') {
      preview = preview
        .replace(/{category_name}/g, 'Kitchen Tools')
        .replace(/{category_description}/g, 'All kitchen essentials')
        .replace(/{parent_category}/g, 'Home & Garden')
        .replace(/{store_name}/g, storeName || 'My Store');
    }
    return preview;

  };

  // Fetch store name for template preview
  useEffect(() => {
    if (!hashSiteId) return;
    fetch(`${apiUrl}/api/seo-optimizer/store-info?id=${hashSiteId}`)
      .then((res) => (res.ok ? res.json() : { name: '' }))
      .then((data) => setStoreName(data?.name ?? ''))
      .catch(() => setStoreName(''));
  }, [hashSiteId, apiUrl]);
  
  // Update preview when templates change
  useEffect(() => {
    if (showTemplateSection) {
      const currentTemplates = getCurrentTemplates();
      setNewTitle(replaceVariables(currentTemplates.title, contentType));
      setNewMeta(replaceVariables(currentTemplates.meta, contentType));
      if (contentType === 'Products' && currentTemplates.alt) {
        setNewAlt(replaceVariables(currentTemplates.alt, contentType));
      }
    }
  }, [productTemplates, categoryTemplates, contentType, showTemplateSection, storeName]);
  
  // Load saved templates for all content types
  useEffect(() => {
    if (!hashSiteId) return;
    if (loadAllTemplatesInFlightRef.current) return;
    loadAllTemplatesInFlightRef.current = true;
    
    const loadAllTemplates = async () => {
      setIsLoadingTemplate(true);
      try {
        // Load templates for Products and Categories in parallel
        const [productsResponse, categoriesResponse] = await Promise.all([
          fetch(`${apiUrl}/api/seo-optimizer/templates?id=${hashSiteId}&type=Products`).catch(() => null),
          fetch(`${apiUrl}/api/seo-optimizer/templates?id=${hashSiteId}&type=Categories`).catch(() => null)
        ]);

        let productConfigured = false;
        let categoryConfigured = false;

        // Load Products template
        if (productsResponse?.ok) {
          const data = await productsResponse.json();
          if (data.success && data.templates && data.templates.length > 0) {
            const template = data.templates[0];
            const title = template.titleTemplate || '';
            const meta = template.metaDescriptionTemplate || '';
            const alt = template.descriptionTemplate || '';
            productConfigured = !!(title?.trim() || meta?.trim() || alt?.trim());
            setProductTemplates({
              title,
              meta,
              alt,
              cruiseControl: template.cruiseControl === true
            });
          }
        }

        // Load Categories template
        if (categoriesResponse?.ok) {
          const data = await categoriesResponse.json();
          if (data.success && data.templates && data.templates.length > 0) {
            const template = data.templates[0];
            const title = template.titleTemplate || '';
            const meta = template.metaDescriptionTemplate || '';
            categoryConfigured = !!(title?.trim() || meta?.trim());
            setCategoryTemplates({
              title,
              meta,
              cruiseControl: template.cruiseControl === true
            });
          }
        }

        // When template is configured, keep section collapsed
        if (productConfigured || categoryConfigured) {
          setShowTemplateSection(false);
        }
      } catch (error) {
        logger.error('Error loading templates:', error);
      } finally {
        setIsLoadingTemplate(false);
        loadAllTemplatesInFlightRef.current = false;
      }
    };
    
    loadAllTemplates();
  }, [hashSiteId, apiUrl]);
  
  // Load cache settings (auto-validate after bulk update) when hashSiteId is available
  useEffect(() => {
    if (!hashSiteId) return;
    if (loadCacheSettingsInFlightRef.current) return;
    loadCacheSettingsInFlightRef.current = true;
    const loadCacheSettings = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/seo-cache/settings?id=${hashSiteId}`);
        if (res.ok) {
          const data = await res.json();
          setAutoValidateAfterBulkUpdate(data.auto_validate_after_bulk_update === true);
        }
      } catch (e) {
        logger.error('Error loading cache settings:', e);
      } finally {
        loadCacheSettingsInFlightRef.current = false;
      }
    };
    loadCacheSettings();
  }, [hashSiteId, apiUrl]);

  // Fetch categories when hashSiteId is available
  useEffect(() => {
    if (!hashSiteId) return;
    if (fetchCategoriesInFlightRef.current) return;
    fetchCategoriesInFlightRef.current = true;
    fetchCategories().finally(() => { fetchCategoriesInFlightRef.current = false; });
  }, [hashSiteId]);
  
  // Fetch products/categories based on selectedEntityType
  useEffect(() => {
    if (!hashSiteId) return;
    if (fetchProductsInFlightRef.current) return;
    fetchProductsInFlightRef.current = true;
    fetchProducts().finally(() => { fetchProductsInFlightRef.current = false; });
  }, [hashSiteId, selectedEntityType, productPage, productLimit, productSearch, productCategory, productListingFilter]);
  
  // Reset selection when entity type changes
  useEffect(() => {
    setSelectedProductIds(new Set());
    setProductPage(1);
    setProductCategory(''); // Reset category filter when switching entity types
  }, [selectedEntityType]);
  
  // Fetch categories list
  const fetchCategories = async () => {
    if (!hashSiteId) return;
    
    setCategoriesLoading(true);
    try {
      const allCategories = [];
      let offset = 0;
      const limit = 100;
      let total = null;
      let hasMore = true;
      
      // Fetch all categories in batches
      while (hasMore) {
        const categoryUrl = `${apiUrl}/api/seo-optimizer/categories?id=${hashSiteId}&limit=${limit}&offset=${offset}`;
        
        const response = await fetch(categoryUrl);
        
        if (!response.ok) {
          logger.error('Failed to fetch categories:', response.status, response.statusText);
          hasMore = false;
          break;
        }
        
        const data = await response.json();
        
        if (data.success !== false && data.categories) {
          if (total === null) {
            total = data.total || 0;
          }
          
          if (data.categories.length > 0) {
            allCategories.push(...data.categories);
            offset += limit;
            hasMore = data.categories.length === limit && allCategories.length < total;
          } else {
            hasMore = false;
          }
        } else {
          logger.error('Invalid categories response:', data);
          hasMore = false;
        }
      }
      
      setCategories(allCategories);
    } catch (error) {
      logger.error('Failed to fetch categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };
  
  // Fetch queue status once on mount
  useEffect(() => {
    if (!hashSiteId) return;
    if (fetchQueueStatusInFlightRef.current) return;
    fetchQueueStatusInFlightRef.current = true;
    fetchQueueStatus().finally(() => { fetchQueueStatusInFlightRef.current = false; });
  }, [hashSiteId]);

  // Poll queue status only while there are active jobs
  const queueActive = queueStatus.pending > 0 || queueStatus.processing > 0;
  useEffect(() => {
    if (!hashSiteId || !queueActive) return;
    const interval = setInterval(fetchQueueStatus, 5000);
    return () => clearInterval(interval);
  }, [hashSiteId, queueActive]);
  
  const fetchProducts = async () => {
    if (!hashSiteId) return;
    
    setIsLoadingProducts(true);
    try {
      let endpoint = '';
      if (selectedEntityType === 'Products') {
        endpoint = 'products';
      } else if (selectedEntityType === 'Categories') {
        endpoint = 'categories';
      } else {
        setIsLoadingProducts(false);
        return;
      }
      
      let url = `${apiUrl}/api/seo-optimizer/${endpoint}?id=${hashSiteId}&limit=${productLimit}&offset=${(productPage - 1) * productLimit}`;
      if (productSearch) {
        url += `&search=${encodeURIComponent(productSearch)}`;
      }
      if (productCategory && selectedEntityType === 'Products') {
        url += `&category=${encodeURIComponent(productCategory)}`;
      }
      if (selectedEntityType === 'Products' && productListingFilter === 'online') {
        url += '&onlineOnly=true';
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        
        if (selectedEntityType === 'Products') {
          setProducts(data.products || []);
          setProductTotal(data.total || 0);
        } else if (selectedEntityType === 'Categories') {
          setProducts(data.categories || []);
          setProductTotal(data.total || 0);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        logger.error(`Error fetching ${selectedEntityType.toLowerCase()}:`, response.status, errorData);
        setProducts([]);
        setProductTotal(0);
      }
    } catch (error) {
      logger.error(`Error fetching ${selectedEntityType.toLowerCase()}:`, error);
      setProducts([]);
      setProductTotal(0);
    } finally {
      setIsLoadingProducts(false);
    }
  };
  
  const fetchQueueStatus = async () => {
    if (!hashSiteId) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/seo-optimizer/queue-status?id=${hashSiteId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success !== false) {
          setQueueStatus({
            pending: data.pending || data.status?.pending || 0,
            processing: data.processing || data.status?.processing || 0,
            completed: data.completed || data.status?.completed || 0,
            failed: data.failed || data.status?.failed || 0
          });
        }
      }
    } catch (error) {
      logger.error('Error fetching queue status:', error);
    }
  };
  
  const saveAutoValidateSetting = async (newValue) => {
    if (!hashSiteId) return;
    try {
      const getRes = await fetch(`${apiUrl}/api/seo-cache/settings?id=${hashSiteId}`);
      const current = getRes.ok ? await getRes.json() : {};
      const res = await fetch(`${apiUrl}/api/seo-cache/settings?id=${hashSiteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_validate_after_bulk_update: newValue,
          auto_validate_after_webhook: current.auto_validate_after_webhook ?? false,
          validation_auto_revalidate_outdated: current.validation_auto_revalidate_outdated ?? false,
        }),
      });
      if (res.ok) showToast(tn('settingSaved'), 'success', 2000);
      else showToast(tn('failedToSaveSetting'), 'error');
    } catch (e) {
      logger.error('Error saving auto-validate setting:', e);
      showToast(tn('failedToSaveSetting'), 'error');
    }
  };

  const handleSaveTemplate = async () => {
    if (!hashSiteId) return;
    
    const currentTemplates = getCurrentTemplates();
    
    setIsSavingTemplate(true);
    
    try {
      // Check if template exists
      const checkResponse = await fetch(`${apiUrl}/api/seo-optimizer/templates?id=${hashSiteId}&type=${contentType}`);
      let templateId = null;
      
      if (checkResponse.ok) {
        const checkData = await checkResponse.json();
        if (checkData.templates && checkData.templates.length > 0) {
          templateId = checkData.templates[0].id;
        }
      }
      
      // If template exists, update it; otherwise create new
      const method = templateId ? 'PUT' : 'POST';
      const url = templateId 
        ? `${apiUrl}/api/seo-optimizer/templates/${templateId}?id=${hashSiteId}`
        : `${apiUrl}/api/seo-optimizer/templates?id=${hashSiteId}`;
      
      const requestBody = {
        templateType: contentType,
        templateName: `Default ${contentType} Template`,
        titleTemplate: currentTemplates.title,
        metaDescriptionTemplate: currentTemplates.meta,
        cruiseControl: contentType === 'Products' ? productTemplates.cruiseControl : categoryTemplates.cruiseControl,
      };
      
      // Only include descriptionTemplate (alt text) for Products
      if (contentType === 'Products') {
        requestBody.descriptionTemplate = currentTemplates.alt || '';
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success || data.template) {
          showToast(tn('templateSavedSuccess'), 'success');
          setShowTemplateSection(false);
        }
      } else {
        const errorData = await response.json();
        showToast(errorData.error || tn('failedToSaveTemplate'), 'error');
      }
    } catch (error) {
      logger.error('Error saving template:', error);
      showToast(tn('failedToSaveTemplate'), 'error');
    } finally {
      setIsSavingTemplate(false);
    }
  };
  
  const insertVariable = (field, variable) => {
    const textareaId = `template-textarea-${field}`;
    const textarea = document.getElementById(textareaId);
    const currentTemplates = getCurrentTemplates();
    
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      let template = '';
      
      if (field === 'title') {
        template = currentTemplates.title;
      } else if (field === 'meta') {
        template = currentTemplates.meta;
      } else {
        template = currentTemplates.alt || '';
      }
      
      const before = template.substring(0, start);
      const after = template.substring(end);
      let variableToInsert = variable;
      if (before.trim().length > 0 && !before.endsWith(' ')) {
        variableToInsert = ' ' + variable;
      }
      
      const updatedTemplate = before + variableToInsert + after;
      if (field === 'title') {
        if (contentType === 'Products') {
          setProductTemplates(prev => ({ ...prev, title: updatedTemplate }));
        } else if (contentType === 'Categories') {
          setCategoryTemplates(prev => ({ ...prev, title: updatedTemplate }));
        }
      } else if (field === 'meta') {
        if (contentType === 'Products') {
          setProductTemplates(prev => ({ ...prev, meta: updatedTemplate }));
        } else if (contentType === 'Categories') {
          setCategoryTemplates(prev => ({ ...prev, meta: updatedTemplate }));
        }
      } else {
        if (contentType === 'Products') {
          setProductTemplates(prev => ({ ...prev, alt: updatedTemplate }));
        }
      }
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variableToInsert.length, start + variableToInsert.length);
      }, 0);
    } else {
      // Fallback: append to end
      let template = '';
      if (field === 'title') {
        template = currentTemplates.title;
      } else if (field === 'meta') {
        template = currentTemplates.meta;
      } else {
        template = currentTemplates.alt || '';
      }
      
      const spacePrefix = template.trim().length > 0 && !template.endsWith(' ') ? ' ' : '';
      const updatedTemplate = template + spacePrefix + variable;
      
      if (field === 'title') {
        if (contentType === 'Products') {
          setProductTemplates(prev => ({ ...prev, title: updatedTemplate }));
        } else if (contentType === 'Categories') {
          setCategoryTemplates(prev => ({ ...prev, title: updatedTemplate }));
        }
      } else if (field === 'meta') {
        if (contentType === 'Products') {
          setProductTemplates(prev => ({ ...prev, meta: updatedTemplate }));
        } else if (contentType === 'Categories') {
          setCategoryTemplates(prev => ({ ...prev, meta: updatedTemplate }));
        }
      } else {
        if (contentType === 'Products') {
          setProductTemplates(prev => ({ ...prev, alt: updatedTemplate }));
        }
      }
    }
  };
  
  const hasContent = (field) => {
    const currentTemplates = getCurrentTemplates();
    if (field === 'title') return currentTemplates.title.trim().length > 0;
    if (field === 'meta') return currentTemplates.meta.trim().length > 0;
    return contentType === 'Products' && currentTemplates.alt && currentTemplates.alt.trim().length > 0;
  };
  
  const handleSelectProduct = (productId) => {
    setSelectedProductIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };
  
  const handleSelectAll = () => {
    if (selectedProductIds.size === products.length) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(products.map(p => p.id)));
    }
  };
  
  const bulkUpdateToastOpts = { anchor: bulkUpdateButtonRef, anchorPosition: 'above' };

  const handleRunTemplate = async () => {
    if (selectedProductIds.size === 0) {
      const typeLabel = selectedEntityType === 'Products' ? tn('products') : tn('categories');
      showToast(tn('selectAtLeastOne', { type: typeLabel }), 'error', 6000, bulkUpdateToastOpts);
      return;
    }
    
    // Use templates for the selectedEntityType
    let templatesToUse;
    if (selectedEntityType === 'Products') {
      templatesToUse = productTemplates;
    } else if (selectedEntityType === 'Categories') {
      templatesToUse = categoryTemplates;
    } else {
      templatesToUse = getCurrentTemplates();
    }
    
    // Determine which fields to update
    const fields = new Set();
    if (templatesToUse.title) fields.add('title');
    if (templatesToUse.meta) fields.add('meta');
    if (selectedEntityType === 'Products' && templatesToUse.alt) fields.add('alt');
    
    if (fields.size === 0) {
      const typeLabel = selectedEntityType === 'Products' ? tn('products') : tn('categories');
      showToast(tn('createTemplateOneField', { type: typeLabel }), 'error', 6000, bulkUpdateToastOpts);
      setContentType(selectedEntityType);
      setShowTemplateSection(true);
      return;
    }
    
    setPendingFields(fields);
    setShowConfirmModal(true);
  };
  
  const confirmRunTemplate = async () => {
    if (!hashSiteId) return;
    
    setShowConfirmModal(false);
    setIsRunningTemplate(true);
    
    try {
      const response = await fetch(`${apiUrl}/api/seo-optimizer/run-template?id=${hashSiteId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateId: null,
          itemIds: Array.from(selectedProductIds),
          itemType: selectedEntityType === 'Products' ? 'product' : 'category'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const typeLabel = selectedEntityType === 'Products' ? tn('products') : tn('categories');
        if (data.success) {
          showToast(tn('queuedForOptimization', { count: selectedProductIds.size, type: typeLabel }), 'success', 4000, bulkUpdateToastOpts);
          setSelectedProductIds(new Set());
          setTimeout(() => fetchQueueStatus(), 1000);
        } else {
          showToast(data.error || tn('failedToQueue', { type: typeLabel }), 'error', 6000, bulkUpdateToastOpts);
        }
      } else {
        const errorData = await response.json();
        const typeLabel = selectedEntityType === 'Products' ? tn('products') : tn('categories');
        showToast(errorData.error || tn('failedToQueue', { type: typeLabel }), 'error', 6000, bulkUpdateToastOpts);
      }
    } catch (error) {
      logger.error('Error running template:', error);
      showToast(tn('failedToQueueProducts'), 'error', 6000, bulkUpdateToastOpts);
    } finally {
      setIsRunningTemplate(false);
    }
  };

  // Fetch all product IDs in a category (handling pagination)
  const fetchAllProductIdsInCategory = async (categoryId) => {
    const allProductIds = [];
    let offset = 0;
    const limit = 250; // Max limit per Ecwid API
    let hasMore = true;

    while (hasMore) {
      try {
        let url = `${apiUrl}/api/seo-optimizer/products?id=${hashSiteId}&limit=${limit}&offset=${offset}&category=${encodeURIComponent(categoryId)}`;
        if (productListingFilter === 'online') {
          url += '&onlineOnly=true';
        }
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          const products = data.products || [];
          
          // Extract product IDs
          products.forEach(product => {
            allProductIds.push(product.id);
          });
          
          // Check if there are more products
          const total = data.total || 0;
          offset += limit;
          hasMore = offset < total;
        } else {
          logger.error('Error fetching products:', response.status);
          hasMore = false;
        }
      } catch (error) {
        logger.error('Error fetching products:', error);
        hasMore = false;
      }
    }

    return allProductIds;
  };

  // Apply template to all products in selected category
  const handleApplyTemplateToCategory = async () => {
    if (!productCategory) {
      showToast(tn('pleaseSelectCategory'), 'error');
      return;
    }

    if (selectedEntityType !== 'Products') {
      showToast(tn('categoryBulkOnlyProducts'), 'error');
      return;
    }

    // Check if templates exist
    const templatesToUse = productTemplates;
    const fields = new Set();
    if (templatesToUse.title) fields.add('title');
    if (templatesToUse.meta) fields.add('meta');
    if (templatesToUse.alt) fields.add('alt');
    
    if (fields.size === 0) {
      showToast(tn('createTemplateOneFieldGeneric'), 'error');
      setContentType('Products');
      setShowTemplateSection(true);
      return;
    }

    // Confirm action
    const categoryName = categories.find(c => c.id === productCategory)?.name || tn('selectedCategory');
    const confirmed = window.confirm(
      tn('confirmApplyCategory', { name: categoryName })
    );

    if (!confirmed) return;

    setIsApplyingToCategory(true);

    try {
      // Fetch all product IDs in the category
      showToast(tn('fetchingProductsInCategory'), 'info');
      const productIds = await fetchAllProductIdsInCategory(productCategory);

      if (productIds.length === 0) {
        showToast(tn('noProductsInCategory'), 'error');
        setIsApplyingToCategory(false);
        return;
      }

      showToast(tn('foundProductsQueuing', { count: productIds.length }), 'info');

      // Apply template to all products
      const response = await fetch(`${apiUrl}/api/seo-optimizer/run-template?id=${hashSiteId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateId: null,
          itemIds: productIds,
          itemType: 'product'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showToast(tn('queuedInCategory', { count: productIds.length, name: categoryName }), 'success');
          setTimeout(() => fetchQueueStatus(), 1000);
        } else {
          showToast(data.error || tn('failedToQueueProductsInCategory'), 'error');
        }
      } else {
        const errorData = await response.json();
        showToast(errorData.error || tn('failedToQueueProductsInCategory'), 'error');
      }
    } catch (error) {
      logger.error('Error applying template to category:', error);
      showToast(tn('failedToApplyTemplateToCategory'), 'error');
    } finally {
      setIsApplyingToCategory(false);
    }
  };
  
  const totalPages = Math.ceil(productTotal / productLimit);
  
  const handleUpdateSingleRecord = async (itemId) => {
    if (!hashSiteId) return;
    
    // Use templates for the selectedEntityType
    let templatesToUse;
    if (selectedEntityType === 'Products') {
      templatesToUse = productTemplates;
    } else if (selectedEntityType === 'Categories') {
      templatesToUse = categoryTemplates;
    } else {
      templatesToUse = getCurrentTemplates();
    }
    
    if (!templatesToUse.title && !templatesToUse.meta && !(selectedEntityType === 'Products' && templatesToUse.alt)) {
      const typeLabel = selectedEntityType === 'Products' ? tn('products') : tn('categories');
      showToast(tn('createTemplateOneField', { type: typeLabel }), 'error');
      setContentType(selectedEntityType);
      setShowTemplateSection(true);
      return;
    }
    
    setUpdatingRecordId(itemId);
    
    try {
      const response = await fetch(`${apiUrl}/api/seo-optimizer/run-template?id=${hashSiteId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateId: null,
          itemIds: [itemId],
          itemType: selectedEntityType === 'Products' ? 'product' : 'category'
        })
      });
      
      const typeLabel = selectedEntityType === 'Products' ? tn('products') : tn('categories');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const typeSingular = selectedEntityType === 'Products' ? tn('productSingular') : tn('categorySingular');
          showToast(tn('templateQueuedForItem', { type: typeSingular, id: itemId }), 'success');
          setTimeout(() => {
            fetchQueueStatus();
            fetchProducts();
          }, 1000);
        } else {
          showToast(data.error || tn('failedToQueue', { type: typeLabel }), 'error');
        }
      } else {
        const errorData = await response.json();
        showToast(errorData.error || tn('failedToQueue', { type: typeLabel }), 'error');
      }
    } catch (error) {
      logger.error('Error updating single record:', error);
      showToast(tn('failedToUpdateRecord'), 'error');
    } finally {
      setUpdatingRecordId(null);
    }
  };

  return (
    <div className="seo-automation">
      {/* Queue Status — always mounted for guided tour target */}
      <div id="bulk-tour-queue" className="mb-4">
        {queueStatus.pending > 0 || queueStatus.processing > 0 ? (
          <div className="alert alert-info mb-0">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{tn('queueStatus')}</strong>
                {queueStatus.pending > 0 && <span className="ms-2">{tn('pending')}: {queueStatus.pending}</span>}
                {queueStatus.processing > 0 && <span className="ms-2">{tn('processing')}: {queueStatus.processing}</span>}
                {queueStatus.completed > 0 && <span className="ms-2 text-success">{tn('completed')}: {queueStatus.completed}</span>}
                {queueStatus.failed > 0 && <span className="ms-2 text-danger">{tn('failed')}: {queueStatus.failed}</span>}
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={fetchQueueStatus}
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="alert alert-light border mb-0 py-2 px-3 small text-muted d-flex justify-content-between align-items-center">
            <span>{t("bulkOptimizeTour.queueIdle")}</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchQueueStatus} title={tn('queueStatus')}>
              <RefreshCw size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Info: Template automation overwrites existing data */}
      {/* <div className="card border-0 shadow-sm mb-3 bg-info bg-opacity-10 border-start border-info border-4">
        <div className="card-body py-3 px-4">
          <ul className="mb-0 ps-3" style={{ fontSize: '1.05rem', lineHeight: 1.6 }}>
            <li className="fw-bold mb-2"><span className="text-dark">This is a template-based automation.</span></li>
            <li className="fw-bold mb-2"><span className="text-dark">Applying templates will overwrite all existing AI-audited data and results</span> (titles, meta descriptions, alt text) for the selected items.</li>
            <li className="fw-bold"><span className="text-dark">Configure at least one field (Title, Meta, or Image Alt) below before applying.</span></li>
          </ul>
        </div>
      </div> */}
      
      {/* Template Configuration Section - toggle only via Edit Template / Close button */}
      {(() => {
        const productHasTitle = !!(productTemplates.title?.trim());
        const productHasMeta = !!(productTemplates.meta?.trim());
        const productHasAlt = !!(productTemplates.alt?.trim());
        const categoryHasTitle = !!(categoryTemplates.title?.trim());
        const categoryHasMeta = !!(categoryTemplates.meta?.trim());
        const anyConfigured = productHasTitle || productHasMeta || productHasAlt || categoryHasTitle || categoryHasMeta;
        return (
          <div id="template-configuration-panel" className="card border-0 shadow-sm mb-4">
            <div 
              className="card-header bg-white border-bottom"
              style={{ 
                backgroundColor: anyConfigured ? '#f0fdf4' : '#fef2f2',
                borderLeft: anyConfigured ? '4px solid #22c55e' : '4px solid #ef4444'
              }}
            >
              <div className="d-flex justify-content-between align-items-center gap-2">
                <div
                  id="bulk-tour-template-header"
                  className="d-flex align-items-center gap-3 flex-grow-1 min-w-0"
                  role="button"
                  tabIndex={0}
                  aria-expanded={showTemplateSection}
                  aria-label={showTemplateSection ? tn('close') : tn('editTemplate')}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowTemplateSection((open) => !open)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowTemplateSection((open) => !open);
                    }
                  }}
                >
                  {showTemplateSection ? (
                    <ChevronUp size={16} className="text-muted" />
                  ) : (
                    <ChevronDown size={16} className="text-muted" />
                  )}
                  <div>
                    <h5 className="fw-semibold mb-0">{tn('templateConfiguration')}</h5>
                    {!showTemplateSection && (
                      <div className="mt-2 d-flex flex-column gap-1">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="small fw-semibold text-muted me-1" style={{ minWidth: '72px' }}>{tn('productsLabel')}</span>
                          <span className={`badge ${productHasTitle ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.65rem' }}>
                            {tn('title')} {productHasTitle && '✓'}
                          </span>
                          <span className={`badge ${productHasMeta ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.65rem' }}>
                            {tn('meta')} {productHasMeta && '✓'}
                          </span>
                          <span className={`badge ${productHasAlt ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.65rem' }}>
                            {tn('alt')} {productHasAlt && '✓'}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="small fw-semibold text-muted me-1" style={{ minWidth: '72px' }}>{tn('categoriesLabel')}</span>
                          <span className={`badge ${categoryHasTitle ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.65rem' }}>
                            {tn('title')} {categoryHasTitle && '✓'}
                          </span>
                          <span className={`badge ${categoryHasMeta ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.65rem' }}>
                            {tn('meta')} {categoryHasMeta && '✓'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={`btn btn-sm ${showTemplateSection ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTemplateSection((open) => !open);
                  }}
                >
                  {showTemplateSection ? tn('close') : tn('editTemplate')}
                </button>
              </div>
            </div>
        
        {showTemplateSection && (
          <div 
            className="card-body p-4" 
            style={{ 
              position: 'relative',
              borderLeft: (hasContent('title') || hasContent('meta') || (contentType === 'Products' && hasContent('alt'))) ? '4px solid #22c55e' : '4px solid #ef4444'
            }}
          >
            {isLoadingTemplate && (
              <div 
                className="d-flex align-items-center justify-content-center"
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 10,
                  borderRadius: '0 0 0.5rem 0.5rem'
                }}
              >
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                  <span className="visually-hidden">{tn('loadingTemplate')}</span>
                </div>
                <span className="small text-muted">{tn('loadingSavedTemplate')}</span>
              </div>
            )}
            
            {/* Choose Content Type */}
            <div className="mb-4">
              <h5 className="fw-semibold mb-3">{tn('chooseContentType')}</h5>
              <div id="bulk-tour-resource-toggle" className="d-flex gap-2" role="group">
                <button
                  type="button"
                  className={`btn ${contentType === 'Products' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setContentType('Products')}
                >
                  {tn('products')}
                </button>
                <button
                  type="button"
                  className={`btn ${contentType === 'Categories' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setContentType('Categories')}
                >
                  {tn('categories')}
                </button>
              </div>
            </div>
            
            {/* Select SEO Element to Optimize */}
            <div id="bulk-tour-template-fields-pills" className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">{tn('selectSeoElement')}</h6>
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">{tn('status')}</span>
                <span className={`badge ${hasContent('title') ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.7rem' }}>
                  {tn('titleTag')} {hasContent('title') && '✓'}
                </span>
                <span className={`badge ${hasContent('meta') ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.7rem' }}>
                  {tn('metaDescription')} {hasContent('meta') && '✓'}
                </span>
                <span className={`badge ${hasContent('alt') ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: '0.7rem' }}>
                  {tn('altText')} {hasContent('alt') && '✓'}
                </span>
              </div>
            </div>
            
            {/* All Template Builders Stacked Vertically */}
            <div className="d-flex flex-column gap-4 mb-4">
              {/* Page Titles Template */}
              <div id="bulk-tour-template-title-block" className="border rounded p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0 fw-semibold">
                    {tn('pageTitles')}
                    {hasContent('title') && <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>✓</span>}
                  </h6>
                  <span className="badge bg-info rounded-pill">{tn('titleTag')}</span>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-2">{tn('usingRule')}</label>
                    <textarea
                      id="template-textarea-title"
                      className="form-control form-control-sm mb-2"
                      rows={2}
                      placeholder={tn('placeholderBuildTemplate')}
                      value={templates.title}
                      onChange={(e) => {
                        if (contentType === 'Products') {
                          setProductTemplates(prev => ({ ...prev, title: e.target.value }));
                        } else if (contentType === 'Categories') {
                          setCategoryTemplates(prev => ({ ...prev, title: e.target.value }));
                        }
                      }}
                      style={{ 
                        fontSize: '0.85rem',
                        borderColor: '#0dcaf0',
                        borderWidth: '2px',
                      }}
                    />
                    <div className="d-flex flex-wrap gap-2">
                      {dynamicVariables.map((variable, index) => (
                        <button
                          key={index}
                          className="btn btn-outline-secondary btn-sm py-1 px-2"
                          onClick={() => insertVariable('title', variable.value)}
                          style={{ fontSize: '0.75rem' }}
                        >
                          <Plus size={8} className="me-1" />
                          {variable.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1" style={{ fontSize: '0.75rem' }}>{tn('livePreview')}</label>
                    <div className="d-flex flex-column gap-2">
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label mb-0 fw-semibold" style={{ fontSize: '0.7rem' }}>{tn('current')}</label>
                          <span className="text-muted" style={{ fontSize: '0.65rem' }}>{currentTitle.length} {tn('chars')}</span>
                        </div>
                        <div 
                          className="p-2 rounded border"
                          style={{ 
                            fontSize: '0.75rem', 
                            backgroundColor: '#f8f9fa',
                            borderColor: '#dee2e6',
                            minHeight: '40px',
                            wordWrap: 'break-word',
                            lineHeight: '1.3'
                          }}
                        >
                          {currentTitle || <span className="text-muted" style={{ fontSize: '0.7rem' }}>{tn('noCurrentTitle')}</span>}
                        </div>
                      </div>
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label mb-0 fw-semibold" style={{ fontSize: '0.7rem' }}>{tn('new')}</label>
                          <span className="text-muted" style={{ fontSize: '0.65rem' }}>{newTitle.length} {tn('chars')}</span>
                        </div>
                        <div 
                          className="p-2 rounded border"
                          style={{ 
                            fontSize: '0.75rem', 
                            backgroundColor: '#e7f3ff',
                            borderColor: '#0dcaf0',
                            borderWidth: '2px',
                            minHeight: '40px',
                            wordWrap: 'break-word',
                            lineHeight: '1.3'
                          }}
                        >
                          {newTitle || <span className="text-muted" style={{ fontSize: '0.7rem' }}>{tn('noPreviewAvailable')}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Meta Descriptions Template */}
              <div id="bulk-tour-template-meta-block" className="border rounded p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0 fw-semibold">
                    {tn('metaDescriptions')}
                    {hasContent('meta') && <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>✓</span>}
                  </h6>
                  <span className="badge bg-warning rounded-pill">{tn('metaDescription')}</span>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-2">{tn('usingRule')}</label>
                    <textarea
                      id="template-textarea-meta"
                      className="form-control form-control-sm mb-2"
                      rows={2}
                      placeholder={tn('placeholderBuildTemplate')}
                      value={templates.meta}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Update template state for the active content type
                        if (contentType === 'Products') {
                          setProductTemplates(prev => ({ ...prev, meta: value }));
                        } else if (contentType === 'Categories') {
                          setCategoryTemplates(prev => ({ ...prev, meta: value }));
                        }
                        // Immediately update live preview for Meta Description
                        setNewMeta(replaceVariables(value, contentType));
                      }}
                      style={{ 
                        fontSize: '0.85rem',
                        borderColor: '#ffc107',
                        borderWidth: '2px',
                      }}
                    />
                    <div className="d-flex flex-wrap gap-2">
                      {dynamicVariables.map((variable, index) => (
                        <button
                          key={index}
                          className="btn btn-outline-secondary btn-sm py-1 px-2"
                          onClick={() => insertVariable('meta', variable.value)}
                          style={{ fontSize: '0.75rem' }}
                        >
                          <Plus size={8} className="me-1" />
                          {variable.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1" style={{ fontSize: '0.75rem' }}>{tn('livePreview')}</label>
                    <div className="d-flex flex-column gap-2">
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label mb-0 fw-semibold" style={{ fontSize: '0.7rem' }}>{tn('current')}</label>
                          <span className="text-muted" style={{ fontSize: '0.65rem' }}>{currentMeta.length} {tn('chars')}</span>
                        </div>
                        <div 
                          className="p-2 rounded border"
                          style={{ 
                            fontSize: '0.75rem', 
                            backgroundColor: '#f8f9fa',
                            borderColor: '#dee2e6',
                            minHeight: '40px',
                            wordWrap: 'break-word',
                            lineHeight: '1.3'
                          }}
                        >
                          {currentMeta || <span className="text-muted" style={{ fontSize: '0.7rem' }}>{tn('noCurrentMeta')}</span>}
                        </div>
                      </div>
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label mb-0 fw-semibold" style={{ fontSize: '0.7rem' }}>{tn('new')}</label>
                          <span className="text-muted" style={{ fontSize: '0.65rem' }}>{newMeta.length} {tn('chars')}</span>
                        </div>
                        <div 
                          className="p-2 rounded border"
                          style={{ 
                            fontSize: '0.75rem', 
                            backgroundColor: '#fff3cd',
                            borderColor: '#ffc107',
                            borderWidth: '2px',
                            minHeight: '40px',
                            wordWrap: 'break-word',
                            lineHeight: '1.3'
                          }}
                        >
                          {newMeta || <span className="text-muted" style={{ fontSize: '0.7rem' }}>{tn('noPreviewAvailable')}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Image Alt Text Template - Only for Products */}
              {contentType === 'Products' && (
                <div id="bulk-tour-template-alt-block" className="border rounded p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0 fw-semibold">
                      {tn('imageAltText')}
                      {hasContent('alt') && <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>✓</span>}
                    </h6>
                    <span className="badge bg-success rounded-pill">{tn('altText')}</span>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-2">{tn('usingRule')}</label>
                      <textarea
                        id="template-textarea-alt"
                        className="form-control form-control-sm mb-2"
                        rows={2}
                        placeholder={tn('placeholderBuildTemplate')}
                        value={templates.alt || ''}
                        onChange={(e) => setProductTemplates(prev => ({ ...prev, alt: e.target.value }))}
                        style={{ 
                          fontSize: '0.85rem',
                          borderColor: '#22c55e',
                          borderWidth: '2px',
                        }}
                      />
                      <div className="d-flex flex-wrap gap-2">
                        {dynamicVariables.map((variable, index) => (
                          <button
                            key={index}
                            className="btn btn-outline-secondary btn-sm py-1 px-2"
                            onClick={() => insertVariable('alt', variable.value)}
                            style={{ fontSize: '0.75rem' }}
                          >
                            <Plus size={8} className="me-1" />
                            {variable.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1" style={{ fontSize: '0.75rem' }}>{tn('livePreview')}</label>
                      <div className="d-flex flex-column gap-2">
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <label className="form-label mb-0 fw-semibold" style={{ fontSize: '0.7rem' }}>{tn('current')}</label>
                            <span className="text-muted" style={{ fontSize: '0.65rem' }}>{currentAlt.length} {tn('chars')}</span>
                          </div>
                          <div 
                            className="p-2 rounded border"
                            style={{ 
                              fontSize: '0.75rem', 
                              backgroundColor: '#f8f9fa',
                              borderColor: '#dee2e6',
                              minHeight: '40px',
                              wordWrap: 'break-word',
                              lineHeight: '1.3'
                            }}
                          >
                            {currentAlt || <span className="text-muted" style={{ fontSize: '0.7rem' }}>{tn('noCurrentAltText')}</span>}
                          </div>
                        </div>
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <label className="form-label mb-0 fw-semibold" style={{ fontSize: '0.7rem' }}>{tn('new')}</label>
                            <span className="text-muted" style={{ fontSize: '0.65rem' }}>{newAlt.length} {tn('chars')}</span>
                          </div>
                          <div 
                            className="p-2 rounded border"
                            style={{ 
                              fontSize: '0.75rem', 
                              backgroundColor: '#dcfce7',
                              borderColor: '#22c55e',
                              borderWidth: '2px',
                              minHeight: '40px',
                              wordWrap: 'break-word',
                              lineHeight: '1.3'
                            }}
                          >
                            {newAlt || <span className="text-muted" style={{ fontSize: '0.7rem' }}>{tn('noPreviewAvailable')}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Cruise Control, Auto-validate, Save Template (BigCommerce parity) */}
            <div id="bulk-tour-template-save-bar" className="d-flex flex-wrap align-items-center gap-4 mt-4 pt-3 border-top">
              <div>
                <label className="form-label mb-0 small fw-bold">{tn('cruiseControl')}</label>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={contentType === 'Products' ? productTemplates.cruiseControl : categoryTemplates.cruiseControl}
                    onChange={(e) => {
                      if (contentType === 'Products') {
                        setProductTemplates(prev => ({ ...prev, cruiseControl: e.target.checked }));
                      } else {
                        setCategoryTemplates(prev => ({ ...prev, cruiseControl: e.target.checked }));
                      }
                    }}
                    style={{ width: '2.5rem', height: '1.25rem', cursor: 'pointer' }}
                  />
                </div>
                <div className="small text-muted mt-1">{tn('autoApplyToNew', { type: contentType.toLowerCase() })}</div>
              </div>
              <div style={{ borderLeft: '1px solid #e9ecef', paddingLeft: '1.5rem' }}>
                <label className="form-label mb-0 small fw-bold">{tn('autoValidateAfterBulk')}</label>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={autoValidateAfterBulkUpdate}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setAutoValidateAfterBulkUpdate(newValue);
                      saveAutoValidateSetting(newValue);
                    }}
                    style={{ width: '2.5rem', height: '1.25rem', cursor: 'pointer' }}
                  />
                </div>
                <div className="small text-muted mt-1">{tn('autoValidateAfterBulkHelp')}</div>
              </div>
              <div className="ms-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveTemplate}
                  disabled={isSavingTemplate}
                >
                  {isSavingTemplate ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {tn('saving')}
                    </>
                  ) : (
                    <>
                      <Save size={16} className="me-2" />
                      {tn('saveTemplate')}
                    </>
                  )}
                </button>
              </div>
            </div>
            
          </div>
        )}
      </div>
        );
      })()}
      
      {/* Product/Category Selection Section */}
      <div id="bulk-tour-table" className="card border-0 shadow-sm">
        <div className="card-header bg-white border-bottom">
          <div className="d-flex align-items-center gap-4">
            {/* Entity Type Selector */}
            <div className="d-flex align-items-center gap-2">
              <label className="text-muted mb-0 fw-semibold" style={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{tn('select')}</label>
              <div className="d-flex gap-2" role="group">
                <button
                  type="button"
                  className={`btn ${selectedEntityType === 'Products' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setSelectedEntityType('Products');
                    setProductPage(1);
                    setSelectedProductIds(new Set());
                  }}
                  style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                >
                  {tn('products')}
                </button>
                <button
                  type="button"
                  className={`btn ${selectedEntityType === 'Categories' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setSelectedEntityType('Categories');
                    setProductListingFilter('all');
                    setProductPage(1);
                    setSelectedProductIds(new Set());
                  }}
                  style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                >
                  {tn('categories')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          {/* Filters Section */}
          <div className="card mb-3 border-0 shadow-sm">
            <div className="card-body py-2 px-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="d-flex align-items-center gap-1 text-muted small fw-semibold me-1">
                  <Filter size={14} strokeWidth={2.5} />
                  {tn('filters')}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ width: 'clamp(120px, 18vw, 200px)' }}
                  placeholder={selectedEntityType === 'Products' ? tn('searchByNameSku') : tn('searchCategories')}
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setProductPage(1);
                  }}
                />
                {selectedEntityType === 'Products' && (
                  <>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto', minWidth: '140px' }}
                      value={productCategory}
                      onChange={(e) => {
                        setProductCategory(e.target.value);
                        setProductPage(1);
                      }}
                      disabled={categoriesLoading || isApplyingToCategory}
                    >
                      <option value="">{tn('allCategories')}</option>
                      {categoriesLoading ? (
                        <option disabled>{t('common.loading')}</option>
                      ) : (
                        categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))
                      )}
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto', minWidth: '110px' }}
                      value={productListingFilter}
                      onChange={(e) => {
                        setProductListingFilter(e.target.value);
                        setProductPage(1);
                      }}
                      aria-label={t('seoOptimizer.productListingFilter')}
                    >
                      <option value="all">{t('seoOptimizer.listingAll')}</option>
                      <option value="online">{t('seoOptimizer.listingOnlineOnly')}</option>
                    </select>
                    {(productSearch || productCategory || productListingFilter !== 'all') && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setProductSearch('');
                          setProductCategory('');
                          setProductListingFilter('all');
                          setProductPage(1);
                        }}
                      >
                        {tn('clear')}
                      </button>
                    )}
                  </>
                )}
                <div className="ms-auto d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    onClick={() => {
                      fetchProducts();
                      fetchQueueStatus();
                    }}
                    disabled={isLoadingProducts}
                    title={tn('refreshTable')}
                  >
                    <RefreshCw size={14} style={isLoadingProducts ? { animation: 'spin 0.8s linear infinite' } : undefined} />
                    {tn('refreshTable')}
                  </button>
                  {selectedEntityType === 'Products' && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                      onClick={handleApplyTemplateToCategory}
                      disabled={!productCategory || isApplyingToCategory || categoriesLoading}
                      title={productCategory ? tn('applyToCategoryTitle') : tn('selectCategoryFirst')}
                    >
                      {isApplyingToCategory ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          {tn('applying')}
                        </>
                      ) : (
                        <>
                          <Play size={14} />
                          {tn('applyToAllInCategory')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Product List */}
          {isLoadingProducts ? (
            <div className="text-center py-5">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {tn('noItemsFound', { type: selectedEntityType === 'Products' ? tn('products') : tn('categories') })}
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleSelectAll}
                  >
                    {selectedProductIds.size === products.length ? (
                      <>
                        <CheckSquare size={16} className="me-1" />
                        {tn('deselectAll')}
                      </>
                    ) : (
                      <>
                        <Square size={16} className="me-1" />
                        {tn('selectAll')}
                      </>
                    )}
                  </button>
                  <span className="ms-3">
                    {tn('selectedCount', { selected: selectedProductIds.size, total: products.length })}
                  </span>
                </div>
                <div id="run-bulk-update-section" className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      fetchProducts();
                      fetchQueueStatus();
                    }}
                    disabled={isLoadingProducts}
                    title={tn('refreshList')}
                  >
                    <RefreshCw size={16} className={isLoadingProducts ? 'spinning' : ''} />
                  </button>
                  <button
                    ref={bulkUpdateButtonRef}
                    className="btn btn-primary"
                    onClick={handleRunTemplate}
                    disabled={selectedProductIds.size === 0 || isRunningTemplate}
                  >
                    {isRunningTemplate ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        {tn('processing')}
                      </>
                    ) : (
                      <>
                        <Play size={16} className="me-2" />
                        {tn('applyTemplateCount', { count: selectedProductIds.size })}
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table className="table table-hover">
                  <thead className="sticky-top bg-light">
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedProductIds.size === products.length && products.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th style={{ width: '60px' }}>{tn('image')}</th>
                      <th>{selectedEntityType === 'Products' ? tn('productName') : selectedEntityType === 'Categories' ? tn('categoryName') : tn('brandName')}</th>
                      <th>{tn('currentTitle')}</th>
                      <th>{tn('currentMeta')}</th>
                      <th>{tn('lastOptimized')}</th>
                      <th style={{ width: '140px' }}>{tn('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      // Only use seoTitle and seoDescription - no fallbacks to title/description
                      const currentTitle = product.seoTitle !== undefined ? product.seoTitle : '';
                      const currentMeta = product.seoDescription !== undefined ? product.seoDescription : '';
                      const lastOptimizedLabel = product.lastOptimized || product.last_optimized?.date || null;

                      return (
                        <tr key={product.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedProductIds.has(product.id)}
                              onChange={() => handleSelectProduct(product.id)}
                            />
                          </td>
                          <td>
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center bg-light text-muted"
                                style={{ width: '40px', height: '40px', borderRadius: '4px', fontSize: '0.7rem' }}
                              >
                                –
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="fw-bold">{product.name || '-'}</div>
                            <small className="text-muted">ID: {product.id}</small>
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>
                            {currentTitle ? (
                              currentTitle
                            ) : (
                              <span className="text-muted">{tn('empty')}</span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>
                            {currentMeta ? (
                              currentMeta
                            ) : (
                              <span className="text-muted">{tn('empty')}</span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.8rem' }}>
                            {lastOptimizedLabel ? (
                              <span className="badge bg-light text-dark border">
                                {typeof lastOptimizedLabel === 'string'
                                  ? lastOptimizedLabel
                                  : tn('recently')}
                              </span>
                            ) : (
                              <span className="text-muted">{tn('never')}</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary d-flex align-items-center"
                              onClick={() => handleUpdateSingleRecord(product.id)}
                              disabled={isRunningTemplate || updatingRecordId === product.id}
                            >
                              {updatingRecordId === product.id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1" />
                                  {tn('updating')}
                                </>
                              ) : (
                                <>
                                  <RefreshCw size={14} className="me-1" />
                                  {tn('update')}
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    {tn('pageXofYTotal', { page: productPage, total: totalPages, totalCount: productTotal })}
                  </div>
                  <div className="btn-group">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setProductPage(prev => Math.max(1, prev - 1))}
                      disabled={productPage === 1}
                    >
                      {tn('previous')}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setProductPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={productPage === totalPages}
                    >
                      {tn('next')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowConfirmModal(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{tn('confirmBulkUpdate')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowConfirmModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>
                  {tn('aboutToApplyBefore')} <strong>{selectedProductIds.size} {selectedEntityType === 'Products' ? tn('products') : tn('categories')}</strong>{tn('aboutToApplyAfter')}
                </p>
                <p>{tn('fieldsToUpdate')}</p>
                <ul>
                  {Array.from(pendingFields).map(field => (
                    <li key={field}>{field === 'title' ? tn('titleTag') : field === 'meta' ? tn('metaDescription') : tn('altText')}</li>
                  ))}
                </ul>
                <p className="text-warning">{tn('thisActionWillUpdate', { type: selectedEntityType === 'Products' ? tn('products') : tn('categories') })}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfirmModal(false)}>
                  {tn('cancel')}
                </button>
                <button type="button" className="btn btn-primary" onClick={confirmRunTemplate}>
                  {tn('confirmAndApply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BulkOptimizeGuidedTour
        run={bulkTourRun}
        onClose={handleBulkTourClose}
        onExit={handleBulkTourExit}
        includeAltTemplateStep={contentType === "Products"}
      />
      {bulkTourReplayFlyout && <BulkTourReplayHintFlyout onFinished={handleBulkTourReplayFlyoutFinished} />}
    </div>
  );
});

export default SEOOptimizerAutomation;
